<template>
  <v-layout
    row
    wrap
  >
    <v-flex>
      <page-tile-title
        :has-back-button="true"
        :page-title="post.title"
      >
        <v-btn
          color="primary"
          :loading="isLoading"
          @click.stop="onSubmitForm"
        >
          <v-icon left>
            save
          </v-icon>
          Save
        </v-btn>
        <!-- end edit button -->
      </page-tile-title>
      <!-- end page-tile-title -->

      <v-layout
        grow
      >
        <v-flex lg8>
          <v-layout column>
            <v-flex mb-2>
              <v-card>
                <v-card-title>
                  <v-layout align-center>
                    <v-switch
                      :value="post.publish"
                      :label="post.publish ? 'Publish' : 'Draft'"
                      @change="onUpdate('publish', $event)"
                    />
                    <!-- end status -->
                    <v-spacer />
                    <!-- end spacer -->
                    <v-chip
                      label
                      dark
                      :color="post.publish ? 'success' : 'error'"
                    >
                      {{ post.publish ? 'Publish' : 'Draft' }}
                    </v-chip>
                    <!-- end published at -->
                  </v-layout>
                </v-card-title>
                <v-divider />
                <v-card-text>
                  <v-layout
                    column
                  >
                    <v-flex>
                      <v-text-field
                        :value="post.title"
                        :rules="titleRules"
                        required
                        label="Title"
                        placeholder="Enter title of the post"
                        @input="onUpdate('title', $event)"
                      />
                    </v-flex>
                    <v-flex>
                      <v-text-field
                        :value="post.slug"
                        :rules="slugRules"
                        required
                        label="Slug"
                        placeholder="Enter slug of the post"
                        @input="onUpdate('slug', $event)"
                      />
                    </v-flex>
                    <v-flex>
                      <v-autocomplete
                        :value="post.category"
                        :items="categories"
                        :return-object="true"
                        hide-selected
                        item-text="title"
                        label="Select Category"
                        placeholder="Choose a category"
                        required
                        clearable
                        @input="onUpdate('category', $event)"
                      />
                    </v-flex>
                    <v-flex>
                      <v-autocomplete
                        :value="post.tag"
                        :items="tags"
                        :return-object="true"
                        hide-selected
                        item-text="title"
                        label="Select Tags"
                        placeholder="Choose a Tags"
                        multiple
                        required
                        chips
                        clearable
                        @input="onUpdate('tag', $event)"
                      />
                    </v-flex>
                  </v-layout>
                </v-card-text>
              </v-card>
              <!-- end card -->
            </v-flex>
            <!-- end flex -->
            <v-flex>
              <content-editor
                :content="post.content"
              />
            </v-flex>
            <!-- end flex -->
          </v-layout>
        </v-flex>
        <!-- end flex -->
        <v-flex lg4>
          <v-layout
            ma-0
            pa-0
            column
          >
            <v-flex
              pa-0
              mb-3
            >
              <v-card>
                <v-toolbar
                  flat
                  color="white"
                >
                  <v-toolbar-title class="subheading font-weight-medium">
                    Featured Image
                  </v-toolbar-title>
                </v-toolbar>
                <v-divider />
                <v-card-text>
                  <v-img
                    :src="post.image ? post.image.object_url : noImage"
                    :lazy-src="post.image ? post.image.object_url : noImage"
                    aspect-ratio="1.7778"
                  />
                </v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <upload-image-dialog />
                  <gallery-dialog :item-object="post.image" />
                </v-card-actions>
              </v-card>
            </v-flex>

            <v-flex
              pa-0
              mb-3
            >
              <v-card>
                <v-toolbar
                  flat
                  color="white"
                >
                  <v-toolbar-title class="subheading font-weight-medium">
                    Excerpt
                  </v-toolbar-title>
                </v-toolbar>
                <v-divider />
                <v-card-text>
                  <v-textarea
                    :value="post.excerpt"
                    :rules="excerptRules"
                    required
                    label="Write summary of the post"
                    @input="onUpdate('excerpt', $event)"
                  />
                </v-card-text>
              </v-card>
            </v-flex>
          </v-layout>
        </v-flex>
        <!-- end flex -->
      </v-layout>
      <!-- end layout -->
    </v-flex>
    <!-- end flex -->
  </v-layout>
  <!-- end layout -->
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import PageTileTitle from '@/components/Utilities/PageTileTitle'
import ContentEditor from './components/Update/ContentEditor'
import { fromThisDate } from '../../../../helpers/utilities'
import UploadImageDialog from './components/Update/UploadImageDialog'
import GalleryDialog from './components/Update/GalleryDialog'
const noImageHasBeenFound = require('../../../../assets/no_image_found.png')
export default {
  components: {
    PageTileTitle,
    ContentEditor,
    UploadImageDialog,
    GalleryDialog
  },
  data: () => ({
    noImage: noImageHasBeenFound,
    titleRules: [
      value => !!value || 'Required',
      value => (value && value.length <= 70) || 'Maximum 70 characters',
      value => (value && value.length >= 4) || 'Minimum 4 characters',
    ],
    slugRules: [
      value => !!value || 'Required',
      value => (value && value.length <= 50) || 'Maximum 50 characters',
      value => (value && value.length >= 4) || 'Minimum 4 characters',
      value => (value && /^[a-z0-9/-]+$/.test(value)) || 'Only lowercase letters, numbers or hypens allowed',
    ],
    excerptRules: [
      value => !!value || 'Required',
      value => (value && value.length <= 154) || 'Maximum 154 characters',
      value => (value && value.length >= 55) || 'Minimum 55 characters',
    ],
    isLoading: false
  }),
  computed: {
    ...mapGetters({
      categories: 'blogCategories/categories',
      tags: 'blogTags/tags',
      post: 'blogPosts/post'
    })
  },
  created () {
    // fetch the data when the view is created and the data is
    // already being observed
    this.getPost(this.$route.params.slug)
  },
  methods: {
    ...mapActions({
      getCategories: 'blogCategories/getCategories',
      getTags: 'blogTags/getTags',
      getPost: 'blogPosts/getPost',
      updatePostItem: 'blogPosts/updatePostItem',
      updatePost: 'blogPosts/updatePost',
    }),
    fromNow (date) {
      return fromThisDate(date)
    },
    onUpdate (key, value) {
      this.updatePostItem({
        key: key,
        value: value
      })
    },
    onSubmitForm () {
      this.isLoading = true
      this.updatePost(this.post)
      .then((response) => {
        if (response.status === 202) {
          this.getPost(this.$route.params.slug)
          .then(() => this.isLoading = false)
        }
      })
    }
  }
}
</script>

<style>
.v-breadcrumbs {
  padding: 18px 0;
}
</style>
